import { isSubtaskContributor, subtaskTimeEntries } from '../subtaskTime'
import type { Campaign, Task, TaskStatus } from '../types'
import { taskMatchesOperation } from './operationScope'

export interface UserProductivitySnapshot {
  seconds: number
  sessions: number
  campaigns: number
  deliverables: number
  completed: number
  activeSessions: number
  averageSecondsPerCompleted: number
  byStatus: Array<{ status: TaskStatus; seconds: number }>
}

function inRange(value: string | undefined, start: string, end: string) {
  if (!value) return false
  const key = value.slice(0, 10)
  return key >= start && key <= end
}

export function tasksForContributor(tasks: Task[], user: string) {
  return tasks.flatMap(task => {
    const subtasks = task.subtasks.filter(subtask => isSubtaskContributor(subtask, user))
    return subtasks.length ? [{ ...task, subtasks }] : []
  })
}

export function buildUserProductivitySnapshot(tasks: Task[], campaigns: Campaign[], operation: string, start: string, end: string, user: string): UserProductivitySnapshot {
  const scopedTasks = tasks.filter(task => taskMatchesOperation(task, campaigns, operation))
  const campaignIds = new Set<string>()
  const deliverableIds = new Set<string>()
  const byStatus = new Map<TaskStatus, number>()
  let seconds = 0
  let sessions = 0
  let completed = 0
  let activeSessions = 0

  scopedTasks.forEach(task => task.subtasks.forEach(subtask => {
    if (!isSubtaskContributor(subtask, user)) return
    const userEntries = subtaskTimeEntries(subtask).filter(entry => entry.user === user)
    const periodEntries = userEntries.filter(entry => inRange(entry.startedOn ?? subtask.completedAt, start, end))
    if (periodEntries.length || inRange(subtask.completedAt, start, end)) {
      campaignIds.add(task.campaignId)
      deliverableIds.add(subtask.id)
    }
    periodEntries.forEach(entry => {
      seconds += entry.seconds
      sessions += 1
      byStatus.set(entry.status, (byStatus.get(entry.status) ?? 0) + entry.seconds)
    })
    if (subtask.status === 'Aprovada' && inRange(subtask.completedAt, start, end)) completed += 1
    activeSessions += userEntries.filter(entry => entry.running).length
  }))

  return {
    seconds,
    sessions,
    campaigns: campaignIds.size,
    deliverables: deliverableIds.size,
    completed,
    activeSessions,
    averageSecondsPerCompleted: completed ? Math.round(seconds / completed) : 0,
    byStatus: [...byStatus.entries()].map(([status, statusSeconds]) => ({ status, seconds: statusSeconds })).sort((left, right) => right.seconds - left.seconds),
  }
}
